import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plane, MapPin, Calendar, Users, ArrowRight, Star, Shield,
  PhoneCall, Search, ArrowLeftRight, Headphones, 
  CreditCard, Tag, ChevronDown, TrendingUp, Zap
} from 'lucide-react';
import { useContact } from '../context/ContactContext';
import AirportAutocomplete from '../components/AirportAutocomplete';
import './HomeNew.css';

const popularRoutes = [
  { from: 'JFK', to: 'LAX', fromCity: 'New York', toCity: 'Los Angeles', price: 149 },
  { from: 'ORD', to: 'MIA', fromCity: 'Chicago', toCity: 'Miami', price: 129 },
  { from: 'SFO', to: 'LHR', fromCity: 'San Francisco', toCity: 'London', price: 449 },
  { from: 'LAX', to: 'NRT', fromCity: 'Los Angeles', toCity: 'Tokyo', price: 599 },
  { from: 'ATL', to: 'CUN', fromCity: 'Atlanta', toCity: 'Cancún', price: 219 },
  { from: 'DFW', to: 'CDG', fromCity: 'Dallas', toCity: 'Paris', price: 489 },
];

const destinations = [
  { city: 'Paris', country: 'France', img: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&h=400&fit=crop', price: 449, tag: 'Romantic' },
  { city: 'Tokyo', country: 'Japan', img: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&h=400&fit=crop', price: 599, tag: 'Adventure' },
  { city: 'Dubai', country: 'UAE', img: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&h=400&fit=crop', price: 399, tag: 'Luxury' },
  { city: 'Cancún', country: 'Mexico', img: 'https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?w=600&h=400&fit=crop', price: 299, tag: 'Beach' },
  { city: 'London', country: 'UK', img: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600&h=400&fit=crop', price: 429, tag: 'Culture' },
  { city: 'Bali', country: 'Indonesia', img: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&h=400&fit=crop', price: 549, tag: 'Tropical' },
];

function HomeNew() {
  const navigate = useNavigate();
  const { contactSettings } = useContact();
  const [tripType, setTripType] = useState('roundtrip');
  const [showPassengers, setShowPassengers] = useState(false);
  const passengerRef = useRef(null);
  const [searchData, setSearchData] = useState({
    from: '', to: '', departDate: '', returnDate: '',
    passengers: { adults: 1, children: 0, infants: 0 },
    cabinClass: 'Economy'
  });

  useEffect(() => {
    const handleClick = (e) => {
      if (passengerRef.current && !passengerRef.current.contains(e.target)) setShowPassengers(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const totalPax = searchData.passengers.adults + searchData.passengers.children + searchData.passengers.infants;

  const handleSearch = () => {
    if (!searchData.from || !searchData.to) { alert('Please select departure and destination'); return; }
    if (!searchData.departDate) { alert('Please select departure date'); return; }
    if (tripType === 'roundtrip' && !searchData.returnDate) { alert('Please select return date'); return; }
    navigate('/flight-results', {
      state: { searchParams: { from: searchData.from, to: searchData.to, departDate: searchData.departDate, returnDate: searchData.returnDate, tripType, passengers: searchData.passengers, cabinClass: searchData.cabinClass } }
    });
  };

  const updatePax = (type, val) => {
    setSearchData(prev => ({ ...prev, passengers: { ...prev.passengers, [type]: Math.max(type === 'adults' ? 1 : 0, val) } }));
  };

  const searchRoute = (from, to) => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    const dep = d.toISOString().split('T')[0];
    const r = new Date(d); r.setDate(r.getDate() + 7);
    navigate('/flight-results', { state: { searchParams: { from, to, departDate: dep, returnDate: r.toISOString().split('T')[0], tripType: 'roundtrip', passengers: { adults: 1, children: 0, infants: 0 }, cabinClass: 'Economy' } } });
  };

  return (
    <div className="hm">
      {/* ── HERO ── */}
      <section className="hm-hero">
        <div className="hm-hero-bg">
          <img src="https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=1600&h=900&fit=crop" alt="" aria-hidden="true" />
          <div className="hm-hero-dim" />
        </div>
        <div className="hm-hero-body">
          <h1>Where will you fly next?</h1>
          <p className="hm-hero-sub">Search hundreds of airlines &middot; Compare prices &middot; Book with confidence</p>

          {/* Search card */}
          <div className="sc">
            <div className="sc-tabs">
              <button className={tripType === 'roundtrip' ? 'on' : ''} onClick={() => setTripType('roundtrip')}>Roundtrip</button>
              <button className={tripType === 'oneway' ? 'on' : ''} onClick={() => setTripType('oneway')}>One-way</button>
            </div>

            <div className="sc-body">
              {/* Row 1: FROM / TO */}
              <div className="sc-route">
                <div className="sf">
                  <MapPin size={18} className="sf-ico" />
                  <div className="sf-wrap">
                    <span className="sf-lbl">From</span>
                    <AirportAutocomplete value={searchData.from} onChange={v => setSearchData({...searchData, from: v})} placeholder="City or airport" />
                  </div>
                </div>
                <button className="sc-swap" onClick={() => setSearchData(p => ({...p, from: p.to, to: p.from}))} aria-label="Swap">
                  <ArrowLeftRight size={18} />
                </button>
                <div className="sf">
                  <MapPin size={18} className="sf-ico" />
                  <div className="sf-wrap">
                    <span className="sf-lbl">To</span>
                    <AirportAutocomplete value={searchData.to} onChange={v => setSearchData({...searchData, to: v})} placeholder="City or airport" />
                  </div>
                </div>
              </div>

              {/* Row 2: DATES + PAX */}
              <div className="sc-details">
                <div className="sf">
                  <Calendar size={18} className="sf-ico" />
                  <div className="sf-wrap">
                    <span className="sf-lbl">Depart</span>
                    <input type="date" value={searchData.departDate} onChange={e => setSearchData({...searchData, departDate: e.target.value})} min={new Date().toISOString().split('T')[0]} />
                  </div>
                </div>
                {tripType === 'roundtrip' && (
                  <div className="sf">
                    <Calendar size={18} className="sf-ico" />
                    <div className="sf-wrap">
                      <span className="sf-lbl">Return</span>
                      <input type="date" value={searchData.returnDate} onChange={e => setSearchData({...searchData, returnDate: e.target.value})} min={searchData.departDate || new Date().toISOString().split('T')[0]} />
                    </div>
                  </div>
                )}
                <div className="sf sf-pax" ref={passengerRef}>
                  <Users size={18} className="sf-ico" />
                  <div className="sf-wrap" onClick={() => setShowPassengers(!showPassengers)} style={{cursor:'pointer'}}>
                    <span className="sf-lbl">Travelers</span>
                    <span className="sf-val">{totalPax} traveler{totalPax > 1 ? 's' : ''}, {searchData.cabinClass} <ChevronDown size={14} /></span>
                  </div>
                  {showPassengers && (
                    <div className="pax-dd">
                      {[{l:'Adults',k:'adults',s:'12+'},{l:'Children',k:'children',s:'2–11'},{l:'Infants',k:'infants',s:'Under 2'}].map(p => (
                        <div className="pax-r" key={p.k}>
                          <div className="pax-info"><strong>{p.l}</strong><small>{p.s}</small></div>
                          <div className="pax-ctr">
                            <button onClick={() => updatePax(p.k, searchData.passengers[p.k] - 1)}>−</button>
                            <span>{searchData.passengers[p.k]}</span>
                            <button onClick={() => updatePax(p.k, searchData.passengers[p.k] + 1)}>+</button>
                          </div>
                        </div>
                      ))}
                      <div className="pax-r pax-cabin">
                        <strong>Cabin</strong>
                        <select value={searchData.cabinClass} onChange={e => setSearchData({...searchData, cabinClass: e.target.value})}>
                          <option>Economy</option>
                          <option>Premium Economy</option>
                          <option>Business</option>
                          <option>First</option>
                        </select>
                      </div>
                      <button className="pax-done" onClick={() => setShowPassengers(false)}>Done</button>
                    </div>
                  )}
                </div>
              </div>

              <button className="sc-search" onClick={handleSearch}>
                <Search size={20} /> Search flights
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <div className="tb">
        <div className="tb-inner">
          <div className="tb-i"><Shield size={18} /><span>Secure booking</span></div>
          <div className="tb-i"><Tag size={18} /><span>Best price guarantee</span></div>
          <div className="tb-i"><Headphones size={18} /><span>24/7 support</span></div>
          <div className="tb-i"><CreditCard size={18} /><span>No hidden fees</span></div>
        </div>
      </div>

      {/* ── POPULAR ROUTES ── */}
      <section className="sec">
        <div className="sec-in">
          <h2 className="sec-t">Popular flight deals</h2>
          <p className="sec-sub">Trending routes with the best prices this week</p>
          <div className="rt-grid">
            {popularRoutes.map((r, i) => (
              <button className="rt" key={i} onClick={() => searchRoute(r.from, r.to)}>
                <div className="rt-route">
                  <div className="rt-pt"><Plane size={14} /><div><strong>{r.from}</strong><small>{r.fromCity}</small></div></div>
                  <ArrowRight size={14} className="rt-arr" />
                  <div className="rt-pt"><MapPin size={14} /><div><strong>{r.to}</strong><small>{r.toCity}</small></div></div>
                </div>
                <div className="rt-price"><small>from</small><strong>${r.price}</strong></div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── DESTINATIONS ── */}
      <section className="sec sec-gray">
        <div className="sec-in">
          <div className="sec-head-row">
            <div><h2 className="sec-t">Explore destinations</h2><p className="sec-sub">Handpicked places to inspire your next trip</p></div>
            <button className="btn-link" onClick={() => navigate('/flights')}>See all <ArrowRight size={16} /></button>
          </div>
          <div className="ds-grid">
            {destinations.map((d, i) => (
              <div className="ds" key={i} onClick={() => navigate('/flights')}>
                <div className="ds-img"><img src={d.img} alt={d.city} loading="lazy" /><span className="ds-tag">{d.tag}</span></div>
                <div className="ds-body">
                  <h3>{d.city}</h3>
                  <span className="ds-co">{d.country}</span>
                  <div className="ds-bot"><span>From <strong>${d.price}</strong></span><ArrowRight size={14} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="sec">
        <div className="sec-in">
          <h2 className="sec-t sec-t-center">Why book with us</h2>
          <p className="sec-sub sec-sub-center">Millions of travelers trust FlyTravelDeals</p>
          <div className="wu-grid">
            {[
              { icon: <TrendingUp size={28} />, t: 'Best Prices', d: 'We compare hundreds of airlines to get you the lowest fares every time.' },
              { icon: <Shield size={28} />, t: 'Secure & Easy', d: 'Bank-level encryption protects your data. Book in under 2 minutes.' },
              { icon: <Zap size={28} />, t: 'Instant Confirmation', d: 'Get your e-ticket immediately after booking. No waiting around.' },
              { icon: <Headphones size={28} />, t: '24/7 Expert Support', d: 'Our travel experts are always ready to help by phone, chat or email.' },
            ].map((w, i) => (
              <div className="wu" key={i}>
                <div className="wu-ico">{w.icon}</div>
                <h3>{w.t}</h3>
                <p>{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta">
        <div className="sec-in">
          <div className="cta-in">
            <div className="cta-left">
              <PhoneCall size={32} />
              <div><h2>Need help booking?</h2><p>Talk to our travel experts for exclusive phone-only deals</p></div>
            </div>
            <div className="cta-right">
              <a href={`tel:${(contactSettings.tfn || '+1-888-859-0441').replace(/[^0-9+]/g, '')}`} className="cta-btn">
                <PhoneCall size={20} />
                <div><small>Call toll-free</small><strong>{contactSettings.tfn || '+1-888-859-0441'}</strong></div>
              </a>
              <span className="cta-avail">Available 24/7 · USA &amp; Canada</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAB */}
      <a href={`tel:${(contactSettings.tfn || '+1-888-859-0441').replace(/[^0-9+]/g, '')}`} className="fab" aria-label="Call">
        <PhoneCall size={24} />
      </a>
    </div>
  );
}

export default HomeNew;
